import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SecurityManager } from '../../security/security-manager';
import { UserService } from '../../service/user.service';
import { UserRes } from '../../response/user.response';

const InvalidPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserRes & { domainUrl: string }>({} as any);

  const fetchUser = async () => {
    const uid = SecurityManager.getUid();
    const [user, err] = await UserService.getUserTestFeedback(uid);
    if (err) {
      return;
    }
    setUser(user);
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const handleGoHome = () => {
    if (user.domainUrl) {
      window.location.href = user.domainUrl;
    } else {
      window.location.href = '/';
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Montserrat, sans-serif',
    background:
      'linear-gradient(160deg, #001a33 0%, #002A52 40%, #0a2440 100%)',
    position: 'relative',
    overflow: 'hidden',
    padding: '2rem',
  };

  const contentWrapperStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 2,
    animation: 'fadeInUp 0.8s ease-out',
  };

  const iconContainerStyle: React.CSSProperties = {
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    background:
      'linear-gradient(135deg, rgba(254, 82, 68, 0.15), rgba(254, 82, 68, 0.05))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '2rem',
    position: 'relative',
    animation: 'pulse 3s ease-in-out infinite',
  };

  const iconRingStyle: React.CSSProperties = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    border: '2px solid rgba(254, 82, 68, 0.3)',
    animation: 'ripple 2s ease-out infinite',
  };

  const brokenLinkIconStyle: React.CSSProperties = {
    width: '64px',
    height: '64px',
    color: '#fe5244',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: '0.75rem',
    textAlign: 'center',
    letterSpacing: '-0.02em',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: 'clamp(0.875rem, 2vw, 1.125rem)',
    color: 'rgba(255, 255, 255, 0.6)',
    textAlign: 'center',
    maxWidth: '420px',
    lineHeight: 1.7,
    marginBottom: '2.5rem',
  };

  const cardStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '2rem',
    marginBottom: '2rem',
    maxWidth: '400px',
    width: '100%',
  };

  const reasonItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.75rem 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.9375rem',
  };

  const bulletStyle: React.CSSProperties = {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2E81F7, #0CBD80)',
    flexShrink: 0,
  };

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
  };

  const primaryButtonStyle: React.CSSProperties = {
    padding: '0.875rem 2rem',
    fontSize: '0.9375rem',
    fontWeight: 600,
    fontFamily: 'Montserrat, sans-serif',
    color: '#ffffff',
    background: 'linear-gradient(135deg, #2E81F7 0%, #1a6cd9 100%)',
    border: 'none',
    borderRadius: '50px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 6px 24px rgba(46, 129, 247, 0.3)',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    padding: '0.875rem 2rem',
    fontSize: '0.9375rem',
    fontWeight: 600,
    fontFamily: 'Montserrat, sans-serif',
    color: 'rgba(255, 255, 255, 0.8)',
    background: 'transparent',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '50px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  };

  const waveStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '30%',
    background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='rgba(46,129,247,0.05)' d='M0,160L48,176C96,192,192,224,288,213.3C384,203,480,149,576,138.7C672,128,768,160,864,181.3C960,203,1056,213,1152,197.3C1248,181,1344,139,1392,117.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'bottom',
    pointerEvents: 'none',
  };

  const cornerAccentStyle = (
    position: 'topLeft' | 'bottomRight',
  ): React.CSSProperties => ({
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background:
      position === 'topLeft'
        ? 'radial-gradient(circle, rgba(46, 129, 247, 0.1) 0%, transparent 70%)'
        : 'radial-gradient(circle, rgba(12, 189, 128, 0.08) 0%, transparent 70%)',
    ...(position === 'topLeft'
      ? { top: '-150px', left: '-150px' }
      : { bottom: '-150px', right: '-150px' }),
    pointerEvents: 'none',
  });

  const BrokenLinkIcon = () => (
    <svg
      style={brokenLinkIconStyle}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      <line x1="2" y1="2" x2="22" y2="22" stroke="#fe5244" strokeWidth="2" />
    </svg>
  );

  const reasons = [
    'The link may have expired',
    'The URL might be mistyped',
    'Access permissions may have changed',
  ];

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes ripple {
            0% { transform: scale(1); opacity: 0.5; }
            100% { transform: scale(1.4); opacity: 0; }
          }
          .invalid-btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 32px rgba(46, 129, 247, 0.4) !important;
          }
          .invalid-btn-secondary:hover {
            background: rgba(255, 255, 255, 0.1) !important;
            border-color: rgba(255, 255, 255, 0.4) !important;
          }
        `}
      </style>

      <div style={cornerAccentStyle('topLeft')} />
      <div style={cornerAccentStyle('bottomRight')} />
      <div style={waveStyle} />

      <div style={contentWrapperStyle}>
        <div style={iconContainerStyle}>
          <div style={iconRingStyle} />
          <BrokenLinkIcon />
        </div>

        <h1 style={titleStyle}>Invalid Link</h1>
        <p style={subtitleStyle}>
          This link appears to be broken or no longer valid. Don't worry, we'll
          help you find your way.
        </p>

        <div style={cardStyle}>
          <p
            style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.8125rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '1rem',
              fontWeight: 600,
            }}
          >
            Possible reasons
          </p>
          {reasons.map((reason, index) => (
            <div
              key={index}
              style={{
                ...reasonItemStyle,
                ...(index === reasons.length - 1
                  ? { borderBottom: 'none' }
                  : {}),
              }}
            >
              <span style={bulletStyle} />
              {reason}
            </div>
          ))}
        </div>

        <div style={buttonGroupStyle}>
          <button
            className="invalid-btn-primary"
            style={primaryButtonStyle}
            onClick={handleGoHome}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Go to Home
          </button>
          <button
            className="invalid-btn-secondary"
            style={secondaryButtonStyle}
            onClick={() => navigate(-1)}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvalidPage;
